import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  AlertController,
  LoadingController,
  NavController,
  PopoverController,
  ToastController,
} from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { map } from 'rxjs/operators';
import { PopoverSimpleComponent } from '@1bill-app/components/popover/popover-simple/popover-simple.component';
import {
  BetterDealProduct,
  BetterDealProductDetails,
} from '@1bill-app/services/better-deal/better-deal.type';
import { PopoverBetterDealProductComponent } from '@1bill-app/components/popover/popover-better-deal-product/popover-better-deal-product.component';
import { PopoverBetterDealProductConsentComponent } from '@1bill-app/components/popover/popover-better-deal-product-consent/popover-better-deal-product-consent.component';
import { ROUTE_BILLS_PAGE, ROUTE_GET_BETTER_DEALS } from '@1bill-app/constants';
import { PopoverBetterDealProductPricingComponent } from '@1bill-app/components/popover/popover-better-deal-product-pricing/popover-better-deal-product-pricing.component';
import { SubSink } from 'subsink';
import { BetterDealService } from '@1bill-app/services/better-deal/better-deal.service';
import { BetterDealQuery } from '@1bill-app/services/better-deal/better-deal.query';
import { _orderBy } from '@1bill-app/helpers/lodash.helper';
import { BetterDealStore } from '@1bill-app/services/better-deal/better-deal.store';

const CNC_PHONE_NUMBER = '1300 685 001';

@Component({
  selector: 'app-get-better-deals-products',
  templateUrl: './get-better-deals-products.page.html',
  styleUrls: ['./get-better-deals-products.page.scss'],
})
export class GetBetterDealsProductsPage implements OnInit {
  isLoading$ = this.betterDealQuery.selectLoading();
  productsLoading: boolean = true;
  products$ = this.betterDealQuery.products$.pipe(
    map((products) => _orderBy(products, ['estTermCost'], 'asc')),
  );
  bill = this.betterDealQuery.getValue().bill;
  state = this.betterDealQuery.getValue().state;
  fuelType = this.betterDealQuery.getValue().fuelType;
  billPeriod = this.betterDealQuery.getBillPeriod();
  selectedProduct: BetterDealProduct;
  selectedProductId: number;
  tariffType: string;
  estUsage: number;
  estConsumption: number;
  isDemandTariff: boolean;
  hasNmi: boolean = this.betterDealQuery.getValue().nmi != null;
  isSingle: boolean = true;
  electricityDistributor: string;
  electricityDistributorId: number;
  gasDistributor: string;
  gasDistributorId: number;
  cncPhone = CNC_PHONE_NUMBER;

  subs = new SubSink();

  constructor(
    private router: Router,
    private navCtrl: NavController,
    private logger: NGXLogger,
    private popoverController: PopoverController,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private betterDealService: BetterDealService,
    private betterDealQuery: BetterDealQuery,
    private betterDealStore: BetterDealStore,
    private toastController: ToastController,
  ) {}

  ngOnInit() {
    this.fetchData();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  async fetchData() {
    const loading = await this.loadingController.create({
      message: 'Loading offers...',
    });
    loading.present();
    this.productsLoading = true;
    this.subs.sink = this.betterDealService.getBetterDealProducts().subscribe({
      next: (res) => {
        this.tariffType = res[0]?.electricityRates?.tariffType;
        this.estUsage = res[0]?.estTermCost;
        this.estConsumption =
          this.fuelType == 'Electricity'
            ? res[0]?.estElectricityConsumption
            : res[0]?.estGasUsage;
        this.isDemandTariff = res[0]?.electricityRates?.demand != 0;
        this.isSingle = !res[0]?.electricityRates?.tariffMatched;
        this.electricityDistributor = res[0]?.electricityRates?.distributor;
        this.electricityDistributorId = res[0]?.electricityRates?.distributorId;
        this.gasDistributor = res[0]?.gasRates?.distributor;
        this.gasDistributorId = res[0]?.gasRates?.distributorId;
        loading.dismiss();
      },
      error: (e) => {
        this.logger.error('unable to fetch products', e);
        loading.dismiss();
        this.showAlert();
      },
      complete: () => {
        this.productsLoading = false;
      },
    });
  }

  async getProductDetails(productId: number) {
    let productDetails: BetterDealProductDetails;
    const loading = await this.loadingController.create({
      message: 'Loading details...',
    });
    await loading.present();
    try {
      await this.betterDealService
        .getProductDetails(productId)
        .toPromise()
        .then((res) => {
          productDetails = res;
        });
      loading.dismiss();
    } catch (err) {
      loading.dismiss();
      this.logger.error('unable to fetch product', err);
    }

    if (productDetails != null) {
      const popover = await this.popoverController.create({
        component: PopoverBetterDealProductComponent,
        cssClass: 'better-deals-product-popover',
        mode: 'ios',
        componentProps: {
          productDetails,
          productId,
          billPeriod: this.billPeriod,
          location: this.betterDealQuery.getValue().suburb,
          state: this.betterDealQuery.getValue().state,
          demand: this.isDemandTariff,
        },
      });
      await popover.present();
      const { data } = await popover.onDidDismiss();

      if (data && data.productIdFromPopover) {
        await this.selectPlan(data.productIdFromPopover);
      }
    } else {
      loading.dismiss();
      this.presentToast('warning', 'Unable to get plan details.');
    }
  }

  goBackToFilters() {
    this.navCtrl.back();
  }

  async selectPlan(productId: number) {
    this.selectedProductId = productId;
    this.subs.sink = this.products$.subscribe((res) => {
      this.selectedProduct = res.find((product) => product.productId == productId);
    });

    const popover = await this.popoverController.create({
      component: PopoverBetterDealProductConsentComponent,
      cssClass: 'onebill-medium-popover',
      mode: 'ios',
      componentProps: {
        productId: this.selectedProduct.productId,
        planName: this.selectedProduct.planName,
        retailerName: this.selectedProduct.retailerName,
      },
    });
    await popover.present();
    const { data } = await popover.onDidDismiss();
    if (data) {
      this.betterDealStore.update({
        product: this.selectedProduct,
        firstName: data.firstName,
        email: data.email,
      });
      //this.betterDealService.saveProduct(this.selectedProduct);
      this.router.navigate([ROUTE_GET_BETTER_DEALS + '/signup']);
    } else {
      this.selectedProductId = 0;
    }
  }

  async presentReferencePricingPopover() {
    let title = 'Reference Pricing';
    let description = [
      `The reference price displayed is for a ${
        this.tariffType ?? 'Single Rate'
      } tariff. The Reference Price is a benchmark comparison point that allows consumers to compare the relative price of different offers.`,
      'The energy regulators have established Reference Prices for each electricity distribution zone. Retailers must compare their offers to the reference price. The Reference Price Comparison does not factor in any solar rebates.',
    ];

    const popover = await this.popoverController.create({
      component: PopoverSimpleComponent,
      cssClass: 'onebill-medium-popover',
      mode: 'ios',
      componentProps: {
        popoverTitle: title, // TODO ADD TO CONSTANTS...
        descriptionArray: description,
        buttonText: 'Close',
        hasBackButton: false,
      },
    });
    await popover.present();
  }

  async presentMeterInfoPopover() {
    let title = 'Reference Pricing';
    let description = [
      `Compare and Connect is authorised to use the Australian Energy Market Operator's market system MSATS on behalf of participating retailers - which uses your existing tariff and metering information to determine pricing and plans that you may be eligible for. `,
      'Please note that your current address may be registered differently on MSATS and we will use the information provided to us to give you a quote. If you think you may have issues locating your meter, please contact our customer service team on 1300 685 001 and have your current bill handy.',
    ];

    const popover = await this.popoverController.create({
      component: PopoverSimpleComponent,
      cssClass: 'onebill-medium-popover',
      mode: 'ios',
      componentProps: {
        popoverTitle: title,
        descriptionArray: description,
        buttonText: 'Close',
        hasBackButton: false,
      },
    });
    await popover.present();
  }

  async presentMarketOfferPopover() {
    const popover = await this.popoverController.create({
      component: PopoverSimpleComponent,
      cssClass: 'onebill-popover-simple',
      mode: 'ios',
      componentProps: {
        popoverTitle: this.state == 'VIC' ? 'VIC Default Offer' : 'Default Market Offer',
        description: '',
      },
    });
    return await popover.present();
  }

  async presentPricingCalculationsPopover() {
    const popover = await this.popoverController.create({
      component: PopoverBetterDealProductPricingComponent,
      cssClass: 'onebill-medium-popover',
      mode: 'ios',
      componentProps: {
        fuelType: this.fuelType,
        billPeriod: this.billPeriod,
        estUsage: this.estConsumption ?? this.estUsage,
        tariffType: this.tariffType,
      },
    });
    await popover.present();
  }

  async showAlert() {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message:
        '<img src="assets/icon/alert/warning.svg" alt="warning-icon" /><h2 class="alert-title">Oops!</h2><p class="alert-text">Something went wrong! Please try again later.</p>',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Got it',
          handler: () => {
            this.navCtrl.navigateRoot([ROUTE_BILLS_PAGE]);
          },
        },
      ],
    });

    await alert.present();
  }

  async presentToast(color: 'success' | 'danger' | 'warning', message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color,
      buttons: [
        {
          icon: 'close-circle-outline',
          role: 'cancel',
        },
      ],
    });
    toast.present();
  }

  public convertNegativePercentageToPositive(x: number) {
    //TODO CREATE PIPE FOR BETTER PERFORMANCE
    return `${Math.abs(x)}%`;
  }

  public getVDOstring(x: number) {
    //TODO CREATE PIPE FOR BETTER PERFORMANCE
    const strOffer = this.state == 'VIC' ? 'VDO' : 'DMO';

    if (x < 0) return 'Below ' + strOffer;
    else if (x == 0) return 'Same as ' + strOffer;
    else return 'Above ' + strOffer;
  }
}
